import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { useCachedData } from './useCachedData';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { createLogger } from '../utils/logger';
import { useToast } from '../context/ToastContext';

const logger = createLogger({ module: 'useGoals', level: 'info' });

export type GoalType = 'return_offer' | 'skill_learning' | 'networking' | 'portfolio' | 'custom';

export interface FocusGoal {
  id: string;
  focus_id: string;
  goal_text: string;
  goal_type: GoalType;
  priority: number;
  created_at: string;
  updated_at: string;
  evidence_target?: number; // Number of evidence pieces needed for 100% completion
  // Extended fields from joins
  evidence_count?: number;
  career_card_count?: number;
  note_count?: number;
  rubric_mappings?: GoalRubricMapping[];
}

export interface GoalRubricMapping {
  id: string;
  goal_id: string;
  focus_id: string;
  rubric_category: string;
  rubric_items: string[] | null;
  created_at: string;
}

interface UseGoalsResult {
  goals: FocusGoal[];
  loading: boolean;
  error: Error | null;
  createGoal: (goal: Omit<FocusGoal, 'id' | 'created_at' | 'updated_at'>) => Promise<FocusGoal | null>;
  updateGoal: (id: string, updates: Partial<FocusGoal>) => Promise<boolean>;
  deleteGoal: (id: string) => Promise<boolean>;
  reorderGoals: (goalIds: string[]) => Promise<boolean>;
  refreshGoals: () => Promise<void>;
}

export function useGoals(focusId: string | null): UseGoalsResult {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [goals, setGoals] = useState<FocusGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch goals with evidence counts and rubric mappings
  const fetchGoals = useCallback(async () => {
    if (!focusId || !user) {
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch goals with related data
      const { data: goalsData, error: goalsError } = await supabase
        .from('user_focus_goals')
        .select(`
          *,
          goal_rubric_mappings (
            id,
            rubric_category,
            rubric_items
          )
        `)
        .eq('focus_id', focusId)
        .order('priority', { ascending: true })
        .order('created_at', { ascending: true });

      if (goalsError) throw goalsError;

      // Fetch evidence counts for each goal
      const goalIds = goalsData?.map(g => g.id) || [];
      const evidenceCounts: Record<string, { total: number; career_cards: number; notes: number }> = {};

      if (goalIds.length > 0) {
        const { data: linksData, error: linksError } = await supabase
          .from('content_links')
          .select('target_id, source_type')
          .in('target_id', goalIds)
          .eq('target_type', 'goal')
          .in('source_type', ['career_card', 'note']);

        if (!linksError && linksData) {
          linksData.forEach(link => {
            if (!evidenceCounts[link.target_id]) {
              evidenceCounts[link.target_id] = { total: 0, career_cards: 0, notes: 0 };
            }
            evidenceCounts[link.target_id].total++;
            if (link.source_type === 'career_card') {
              evidenceCounts[link.target_id].career_cards++;
            } else if (link.source_type === 'note') {
              evidenceCounts[link.target_id].notes++;
            }
          });
        }
      }

      // Combine data
      const enrichedGoals = (goalsData || []).map(goal => ({
        ...goal,
        evidence_count: evidenceCounts[goal.id]?.total || 0,
        career_card_count: evidenceCounts[goal.id]?.career_cards || 0,
        note_count: evidenceCounts[goal.id]?.notes || 0,
        rubric_mappings: goal.goal_rubric_mappings || []
      }));

      logger.debug('Goals fetched successfully', { count: enrichedGoals.length });
      return enrichedGoals;
    } catch (err) {
      logger.error('Failed to fetch goals', err);
      throw err;
    }
  }, [focusId, user]);

  // Use cached data for performance
  const { data: cachedGoals, error: cacheError, loading: cacheLoading, refresh: refreshGoals } = useCachedData(
    fetchGoals,
    {
      cacheKey: `${CACHE_KEYS.FOCUS_GOALS}-${focusId || ''}`,
      maxAge: CACHE_DURATIONS.FOCUS_GOALS,
      enabled: !!focusId && !!user,
      skipCache: !focusId
    }
  );

  useEffect(() => {
    if (cachedGoals) {
      setGoals(cachedGoals as FocusGoal[]);
    }
    setLoading(cacheLoading);
    setError(cacheError);
  }, [cachedGoals, cacheLoading, cacheError]);

  // Create a new goal
  const createGoal = useCallback(async (
    goal: Omit<FocusGoal, 'id' | 'created_at' | 'updated_at'>
  ): Promise<FocusGoal | null> => {
    if (!user || !focusId) return null;

    try {
      const { data, error } = await supabase
        .from('user_focus_goals')
        .insert({
          focus_id: focusId,
          goal_text: goal.goal_text,
          goal_type: goal.goal_type,
          priority: goal.priority,
          evidence_target: goal.evidence_target
        })
        .select()
        .single();

      if (error) throw error;

      showToast('Goal created successfully', 'success');
      await refreshGoals();
      return data;
    } catch (err) {
      logger.error('Failed to create goal', err);
      showToast('Failed to create goal', 'error');
      return null;
    }
  }, [user, focusId, showToast, refreshGoals]);

  // Update an existing goal
  const updateGoal = useCallback(async (
    id: string,
    updates: Partial<FocusGoal>
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_focus_goals')
        .update({
          goal_text: updates.goal_text,
          goal_type: updates.goal_type,
          priority: updates.priority,
          evidence_target: updates.evidence_target
        })
        .eq('id', id);

      if (error) throw error;

      showToast('Goal updated successfully', 'success');
      await refreshGoals();
      return true;
    } catch (err) {
      logger.error('Failed to update goal', err);
      showToast('Failed to update goal', 'error');
      return false;
    }
  }, [user, showToast, refreshGoals]);

  // Delete a goal
  const deleteGoal = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_focus_goals')
        .delete()
        .eq('id', id);

      if (error) throw error;

      showToast('Goal deleted successfully', 'success');
      await refreshGoals();
      return true;
    } catch (err) {
      logger.error('Failed to delete goal', err);
      showToast('Failed to delete goal', 'error');
      return false;
    }
  }, [user, showToast, refreshGoals]);

  // Reorder goals by updating priorities
  const reorderGoals = useCallback(async (goalIds: string[]): Promise<boolean> => {
    if (!user) return false;

    try {
      // Update priorities based on new order
      const updates = goalIds.map((id, index) => ({
        id,
        priority: index + 1
      }));

      // Use a transaction to update all priorities
      const promises = updates.map(({ id, priority }) =>
        supabase
          .from('user_focus_goals')
          .update({ priority })
          .eq('id', id)
      );

      const results = await Promise.all(promises);
      const hasError = results.some(result => result.error);

      if (hasError) {
        throw new Error('Failed to update some goal priorities');
      }

      await refreshGoals();
      return true;
    } catch (err) {
      logger.error('Failed to reorder goals', err);
      showToast('Failed to reorder goals', 'error');
      return false;
    }
  }, [user, showToast, refreshGoals]);

  return {
    goals,
    loading,
    error,
    createGoal,
    updateGoal,
    deleteGoal,
    reorderGoals,
    refreshGoals
  };
}